import type { MixDecision } from '@sw/engine'
import type { ReactNode } from 'react'

export interface SoundRadarProps {
  mix: MixDecision | undefined
  headingDeg: number
  /** Furthest source drawn, in metres. */
  rangeM?: number
}

/**
 * What is around you, drawn.
 *
 * Built for listeners who cannot rely on hearing direction — an audio-only
 * world is otherwise closed to them — and it turns out to be the fastest way
 * for anyone to see whether the mix is doing what the rules intended, so it is
 * on the player screen rather than hidden in a developer view.
 */
export function SoundRadar({ mix, headingDeg, rangeM = 120 }: SoundRadarProps): ReactNode {
  const size = 240
  const centre = size / 2
  const radius = centre - 18

  const voices = (mix?.voices ?? [])
    .filter((voice) => voice.audible && voice.spatialization !== 'nonDiegetic')
    .slice(0, 24)

  return (
    <svg
      className="radar"
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={
        voices.length === 0
          ? 'Nothing audible nearby'
          : `${voices.length} sounds around you`
      }
    >
      <circle cx={centre} cy={centre} r={radius} fill="#101722" stroke="#26303f" />
      <circle cx={centre} cy={centre} r={radius * 0.66} fill="none" stroke="#1d2634" />
      <circle cx={centre} cy={centre} r={radius * 0.33} fill="none" stroke="#1d2634" />
      <line x1={centre} y1={centre - radius} x2={centre} y2={centre + radius} stroke="#1d2634" />
      <line x1={centre - radius} y1={centre} x2={centre + radius} y2={centre} stroke="#1d2634" />

      {/* Ahead is always up: the picture turns with the listener. */}
      <text x={centre} y={16} textAnchor="middle" fill="#6b7688" fontSize="10">
        AHEAD
      </text>

      {voices.map((voice) => {
        const reach = Math.min(1, voice.distanceM / rangeM)
        const angle = (voice.azimuthDeg * Math.PI) / 180
        const x = centre + Math.sin(angle) * radius * reach
        const y = centre - Math.cos(angle) * radius * reach
        // Loudness reads as size, so a distant murmur and a close voice are
        // told apart at a glance rather than by counting rings.
        const loudness = Math.max(0.12, Math.min(1, voice.gain))
        return (
          <g key={voice.emitterId}>
            <circle cx={x} cy={y} r={4 + loudness * 9} fill="#4fd1c5" opacity={0.14} />
            <circle cx={x} cy={y} r={2 + loudness * 4} fill="#4fd1c5" opacity={0.55 + loudness * 0.45} />
          </g>
        )
      })}

      <g transform={`translate(${centre} ${centre})`}>
        <path d="M 0 -9 L 6 7 L 0 3 L -6 7 Z" fill="#e8ecf1" />
      </g>

      <text x={centre} y={size - 4} textAnchor="middle" fill="#6b7688" fontSize="9">
        {Math.round(headingDeg)}° · {rangeM} m
      </text>
    </svg>
  )
}
